/// <reference path="../../typings/browser.d.ts" />

namespace Application.Models {
    'use strict';

    import StreamStatusService = Application.Services.StreamStatusService;
    import FlexCollectorService = Application.Services.FlexCollectorService;
    import IStompSubscription = Application.Services.IStompSubscription;

    const DEFAULT_BITRATE_PARAM: string = "bitrate";
    const PROBED_BITRATE_PARAM: string = "probedBitrate";
    const SYNTHETIC_TIMER: number = 9000;

    export class TrendingInfo {
        public plotSeries: IPlotPoint[][];
        public lastValidBitrates: Models.IBitRateTrending[] = [];
        public lastBitrates: Models.IBitRateTrending[] = [];
        public lastValidPoints: IPlotPoint[] = [];
        public lastRealPoint: IPlotPoint[] = [];
        public currentBitrate: string = "- Mbps";
        private trendingSubscription: IStompSubscription;
        private lastValidDates: number[] = [];
        private hrefType: string;
        private hrefId: string;
        private noDataTimers: angular.IPromise<void>[];

        constructor(
            private streamStatusService: StreamStatusService,
            private flexCollectorService: FlexCollectorService,
            private $timeout: angular.ITimeoutService,
            private $q: ng.IQService,
            private deviceId: string,
            private maxGraphSeconds: number,
            private showTrending: boolean,
            private currentBitrateUpdated: (seriesIndex: number) => void,
            private bitrateParams: string[],
            private syntheticTimeout:number = SYNTHETIC_TIMER) {
                if (this.bitrateParams === undefined) {
                    this.bitrateParams = [DEFAULT_BITRATE_PARAM];
                }
                let split = this.deviceId.split("/");
                this.hrefType = split[0];
                this.hrefId = split[1];
                this.bitrateParams.forEach(() => {
                    this.lastValidDates.push(0);
                    this.lastValidPoints.push(undefined);
                    this.lastRealPoint.push(undefined);
                    this.lastBitrates.push(null);
                });
        }

        /**
         * This method loads the initial bitrateData and then subscribes to stomp events
         */
        public initTrendingData(): void {
            this.initialLoadBitrates()
                .then((trendings: Models.IBitRateTrending[][]) => {
                    this.noDataTimers = new Array(trendings.length);
                    this.plotSeries = trendings.map((trending: Models.IBitRateTrending[], index: number) => {
                        if (trending.length > 0) {
                            const series: IPlotPoint[] = this.setTrendingData(trending, index);
                            let lastValidIndex: number = trending.length - 1;
                            this.lastBitrates[index] = trending[lastValidIndex];
                            let lastValidBitrate: IBitRateTrending = this.lastBitrates[index];
                            while (lastValidIndex > 0 && lastValidBitrate.synthetic && lastValidBitrate.bitrate !== null) {
                                lastValidIndex--;
                                lastValidBitrate = trending[lastValidIndex];
                            }
                            if (lastValidBitrate.synthetic && lastValidBitrate.bitrate !== null)
                                lastValidBitrate = undefined;
                            this.lastValidBitrates[index] = lastValidBitrate;
                            this.lastValidDates[index] = new Date().getTime();
                            this.noDataTimers[index] = this.$timeout(() => {
                                this.addSyntethic(index);
                            }, this.syntheticTimeout);
                            return series;
                        }
                    });
                    this.plotSeries.forEach((series: IPlotPoint[], seriesIndex) =>  {
                        if (series && series.length > 0) {
                            this.currentBitrate = this.formatMbps(series[series.length - 1].value);
                            if (this.currentBitrateUpdated)
                                this.currentBitrateUpdated(seriesIndex);
                        }
                    });

                    this.trendingSubscription =
                        this.flexCollectorService.subscribeDeviceParamsBySimplifiedHref(this.hrefType, this.hrefId,
                            (deviceParams: Models.DeviceParams) => {

                                this.bitrateParams.forEach((bitrateParamName: string, index: number) => {

                                    const bitrateParam: DeviceParam = deviceParams.getParam(bitrateParamName);
                                    if (bitrateParam) {
                                        const newBitrate: IBitRateTrending = {
                                            date: bitrateParam.lastUpdate,
                                            bitrate: bitrateParam.lastValue as number,
                                            minValue: undefined,
                                            maxValue: undefined,
                                            synthetic: false,
                                            frontSynth: false,
                                        };
                                        if (this.isNewPoint(newBitrate, index)) {
                                            this.lastValidBitrates[index] = newBitrate;
                                            this.lastValidDates[index] = new Date().getTime();
                                            if (this.noDataTimers[index]) {
                                                this.$timeout.cancel(this.noDataTimers[index]);
                                            }
                                            this.noDataTimers[index] = this.$timeout(() =>{
                                                this.addSyntethic(index);
                                            }, this.syntheticTimeout);
                                        }

                                        this.addTrendingPoint(newBitrate, index);
                                    }
                                });

                            });

                });

        }

        private initialLoadBitrates(): ng.IPromise<IBitRateTrending[][]> {
            return this.$q.all(this.bitrateParams.map((bitratePram: string) => {
                if (bitratePram  === DEFAULT_BITRATE_PARAM && this.hrefType === 'blocks')
                    return this.streamStatusService.getBlockTrendingData(+this.hrefId, undefined, this.maxGraphSeconds);
                else if (this.hrefType === 'streamPipe')
                   return this.streamStatusService.getStreamPipeTrendingDataSeconds("" + this.hrefId, undefined, this.maxGraphSeconds)
                       .then((trendingResult: Models.ITrendingResult) => {
                           //TODO: Maybe check periodically. If the device is the source, maybe the sink gets bitrate
                           // at some point in time
                           const typeAndId: string[] = trendingResult.device.split("/");
                           this.hrefType = typeAndId[0];
                           this.hrefId = typeAndId[1];
                           return trendingResult.values;
                       });
                else
                    return this.streamStatusService.getParamTrendingData(this.deviceId, bitratePram, undefined, this.maxGraphSeconds)
                       .then((result: Models.ITrendingResult) => result.values);
            }));

        }

        public close(): void {
            this.currentBitrateUpdated = undefined;
            if (this.trendingSubscription) {
                this.trendingSubscription.unsubscribe();
                this.trendingSubscription = undefined;
            }
            this.currentBitrate = "- Mbps";
            if (this.noDataTimers !== undefined) {
                this.noDataTimers.forEach((timer) => {
                    if (timer)
                        this.$timeout.cancel(timer);
                });
            }
        }

        public closeAfterReconection(): void{
            if (this.trendingSubscription) {
                this.trendingSubscription.unsubscribe();
            }
        }

        private addSyntethic(index: number): void {
            const dateString: string = new Date().toJSON();
            this.$timeout.cancel(this.noDataTimers[index]);
            this.noDataTimers[index] = this.$timeout(() =>{
                this.addSyntethic(index);
            }, this.syntheticTimeout);
            //Add No Data
            if (this.lastValidBitrates[index]) {
                this.setTrendingData([{
                    date: dateString,
                    bitrate: this.lastValidBitrates[index].bitrate,
                    minValue: undefined,
                    maxValue: undefined,
                    synthetic: true,
                    frontSynth: true,
                }], index);
                if (this.currentBitrateUpdated)
                    this.currentBitrateUpdated(index);
            } else {
                this.setTrendingData([{
                    date: dateString,
                    bitrate: 0,
                    minValue: undefined,
                    maxValue: undefined,
                    synthetic: true,
                    frontSynth: true,
                }], index);
                if (this.currentBitrateUpdated)
                    this.currentBitrateUpdated(index);
            }
        }

        public clearProbedData(): void {
            const index: number = this.bitrateParams.indexOf(PROBED_BITRATE_PARAM);
            if (index >= 0) {
                this.bitrateParams.splice(index, 1);
                this.lastValidBitrates[index] = {
                    date: this.lastValidDates[index].toString(),
                    bitrate: undefined,
                    minValue: undefined,
                    maxValue: undefined,
                    synthetic: false,
                    frontSynth: false,
                };
                this.lastValidPoints[index] = undefined;
            }
        }

        public setShowTrending(val: boolean): void {
            this.showTrending = val;
            this.initTrendingData();
        }

        private setTrendingData(trending: Models.IBitRateTrending[], seriesIndex: number): IPlotPoint[] {
            let initialTrending: Models.IBitRateTrending[] = [];
            for (let point of trending) {
                this.addPointIfIsNew(point, initialTrending, seriesIndex);
            }
            return this.plotGraph(initialTrending, seriesIndex);
        }

        private addTrendingPoint(point: Models.IBitRateTrending, seriesIndex: number): void {
            const changeToValid = this.lastBitrates[seriesIndex] && this.lastBitrates[seriesIndex].frontSynth && !point.frontSynth
                && Date.parse(point.date) > (this.lastRealPoint[seriesIndex] ? this.lastRealPoint[seriesIndex].time : 0);
            if (changeToValid || this.isNewPoint(point, seriesIndex)) {
                this.lastBitrates[seriesIndex] = point;
                const time = Date.parse(point.date);
                this.lastValidPoints[seriesIndex] = {
                        time: time,
                        value: this.bitsToMbps(point.bitrate),
                        synthetic: !!point.frontSynth,
                };
                if(!!point.frontSynth === false){
                    this.lastRealPoint[seriesIndex] = this.lastValidPoints[seriesIndex]
                }
                this.currentBitrate = this.formatMbps(point.bitrate);
                if (this.currentBitrateUpdated)
                    this.currentBitrateUpdated(seriesIndex);
            }
        }

        private addPointIfIsNew(point: Models.IBitRateTrending, initialTrending: Models.IBitRateTrending[], seriesIndex: number): void {
            if (this.isNewPoint(point, seriesIndex)) {
                initialTrending.push(point);
                this.lastBitrates[seriesIndex] = point;
                if(!point.frontSynth) {
                    const time = Date.parse(point.date);
                    this.lastRealPoint[seriesIndex] =  this.lastValidPoints[seriesIndex] = {
                        time: time,
                        value:  this.bitsToMbps(point.bitrate),
                        synthetic: false,
                    };

                } else {
                    const time = Date.parse(point.date);
                    this.lastValidPoints[seriesIndex] = {
                        time: time,
                        value: this.bitsToMbps(point.bitrate),
                        synthetic: true,
                    };
                }
            }
        }

        private isNewPoint(point: Models.IBitRateTrending, seriesIndex: number) {
            const last: IBitRateTrending = this.lastBitrates[seriesIndex];
            let lastDate: Date = last ? new Date(last.date) : new Date(0);
            let newDate: Date = new Date(point.date);
            return newDate > lastDate;
        }

        private shiftTrendingByLength(maxGraphSeconds: number, initialTrending: Models.IBitRateTrending[]): void {
            if (initialTrending.length >= ((maxGraphSeconds) / 5)) {
                initialTrending.shift();
            }
        }

        private shiftTrendingIfBiggerThan(maxGraphSeconds: number, initialTrending: Models.IBitRateTrending[]): void {
            if (initialTrending .length > 0) {
                let oldestDate: Date = new Date(initialTrending[0].date);
                let newDate: Date = new Date(initialTrending[initialTrending.length - 1].date);
                while ((newDate.getTime() - oldestDate.getTime()) > maxGraphSeconds * 1000) {
                    oldestDate = new Date(initialTrending.shift().date);
                }
            }
        }

        private plotGraph(initialTrending: Models.IBitRateTrending[], seriesIndex: number): IPlotPoint[] {
            if (initialTrending === undefined || initialTrending.length === 0) {
                return;
            }
            let dataToPlot: IPlotPoint[] = [];
            initialTrending.forEach((bitRateTrending: Models.IBitRateTrending) => {
                const time = Date.parse(bitRateTrending.date);
                let valueMbps: number = this.bitsToMbps(bitRateTrending.bitrate);
                dataToPlot.push({
                    time: time,
                    value: valueMbps,
                    synthetic: false,
                });
            });


            if (dataToPlot[0] && dataToPlot[0].time) {
                return dataToPlot;
            } else {
                return undefined;
            }
        }

        private bitsToMbps(bits: number): number {
            if (bits === null) {
                return null
            }
            if (bits !== undefined)
                return parseFloat((bits / 1000000).toFixed(2));
            else
                return undefined;
        }

        private formatMbps(mbits: number): string {
            if (mbits !== undefined && mbits !== null)
                return this.bitsToMbps(mbits) + ' Mbps';
            else
                return '-- Mbps';
        }

    }

}
